import React, { CSSProperties, useContext } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';

import { DnDNodeProps } from './DnDNode';
import { addChildNode, removeNode, replaceNode } from './project-dnd.util';

export interface DndProps {
  from: DnDNodeProps;
  to: DnDNodeProps;
  position: 'node' | 'next' | 'prev';
}

export interface ProjectDnDStyles {
  draggerStyle: (isDragging: boolean) => CSSProperties;
  dropperPrevStyle: (isOver: boolean, canDrop: boolean) => CSSProperties;
  dropperNextStyle: (isOver: boolean, canDrop: boolean) => CSSProperties;
  nodeStyle: (
    isDragging: boolean,
    isOver: boolean,
    canDrop: boolean
  ) => CSSProperties;
  containerStyle: CSSProperties;
}

const defaultStyles: ProjectDnDStyles = {
  draggerStyle: (isDragging: boolean) => ({
    opacity: isDragging ? 0 : 1,
    transition: 'all 100ms',
  }),
  dropperPrevStyle: (isOver: boolean, canDrop: boolean) => ({
    height: isOver && canDrop ? 60 : 5,
    border: isOver && canDrop ? '1px dashed #aeaeae' : 'transparent',
    borderRadius: 4,
    transition: 'all 100ms',
  }),
  dropperNextStyle: (isOver: boolean, canDrop: boolean) => ({
    height: isOver && canDrop ? 60 : 5,
    border: isOver && canDrop ? '1px dashed #aeaeae' : 'transparent',
    borderRadius: 4,
    transition: 'all 100ms',
  }),
  nodeStyle: (isDragging: boolean, isOver: boolean, canDrop: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    alignTasks: 'center',
    backgroundColor: !isDragging
      ? isOver
        ? canDrop
          ? '#b4ddff'
          : '#f5f5f5'
        : 'transparent'
      : '#f5f5f5',
    // borderRadius: 4,
    // paddingLeft: 16,
    // paddingRight: 16,
    cursor: 'pointer',
    transition: 'all 100ms',
  }),
  containerStyle: {
    paddingLeft: 40,
    paddingTop: 1,
    paddingBottom: 1,
  },
};

export interface ProjectDnDContextType {
  itemsRef?: React.MutableRefObject<{}>;
  tree?: DnDNodeProps;
  styles?: ProjectDnDStyles;
  onMoveComplete?: (payload: DndProps) => void;
  onClickAway?: () => void;
  onContextMenu?: (e: any, payload: any) => void;
  onNodeClick?: (payload: DnDNodeProps) => void;
  onNodeDoubleClick?: (payload: DnDNodeProps) => void;
  moveNode: (payload: DndProps) => void;
  isDroppable: (payload: DndProps) => boolean;
  canAccept: (node: DnDNodeProps) => string[];
  render?: (props: any, children: Array<DnDNodeProps>) => React.ReactNode;
  disabled?: boolean;
}

export const ProjectDnDContext = React.createContext<ProjectDnDContextType>({
  moveNode: () => {},
  isDroppable: () => false,
  canAccept: () => [],
});

export interface ProjectDnDProviderProps {
  onMoveComplete?: (payload: DndProps) => void;
  onClickAway?: () => void;
  onContextMenu?: (e: any, payload: any) => void;
  onNodeClick?: (payload: DnDNodeProps) => void;
  onNodeDoubleClick?: (payload: DnDNodeProps) => void;
  itemsRef?: React.MutableRefObject<{}>;
  tree?: DnDNodeProps;
  styles?: ProjectDnDStyles;
  render?: (
    props: DnDNodeProps,
    children: Array<DnDNodeProps>
  ) => React.ReactNode;
  children?: any;
  disabled?: boolean;
}

export function ProjectDnDProvider({
  onMoveComplete = payload => {},
  onClickAway = () => {},
  onContextMenu = (e: any, payload: any) => {},
  onNodeClick = payload => {},
  onNodeDoubleClick = payload => {},
  tree,
  styles = defaultStyles,
  render,
  children,
  disabled,
  itemsRef,
}: ProjectDnDProviderProps) {
  const [_tree, setProjectDnD] = React.useState<DnDNodeProps | undefined>(tree);
  React.useEffect(() => {
    //update state on prop change
    setProjectDnD(tree);
  }, [tree]);
  const moveNode = async ({ from, to, position }: DndProps) => {
    if (!_tree || !to.id || !to.nodeType || disabled) return;

    // check that we are not dropping into one of our children
    if (_tree && to.id && to.nodeType && isNotChild({ from, to, position })) {
      const tree = { ..._tree };
      removeNode(tree, from);
      switch (position) {
        case 'node': {
          addChildNode(tree, from, to);
          break;
        }
        default: {
          replaceNode(tree, from, to, position);
        }
      }

      setProjectDnD(tree);
      await onMoveComplete({ from, to, position });
    }
  };

  const isNotChild = ({ from, to, position }: DndProps): boolean => {
    // check that we are not dropping into one of our children
    for (let i = 0; i < from.children.length; i++) {
      if (
        to.id === from.children[i].id &&
        to.nodeType === from.children[i].nodeType
      ) {
        return false;
      } else if (from.children[i].children.length > 0) {
        return isNotChild({ from: from.children[i], to: to, position });
      }
    }
    return true;
  };

  const isDroppable = ({ from, to, position }: DndProps) => {
    if (
      disabled ||
      !_tree || // Invalid tree
      !from.id ||
      !from.nodeType || // Invalid from node
      !to.id ||
      !to.nodeType || // Invalid to node
      from.nodeType === 'project' || // Can't drop a project anywhere
      (from.id === to.id && from.nodeType === to.nodeType) || // From and to are equal
      // Stages can only be dropped 'next' or 'prev' to other stages
      (from.nodeType === 'stage' &&
        to.nodeType === 'stage' &&
        position === 'node') ||
      // Groupings can only be dropped 'next' or 'prev' to other groupings
      (from.nodeType === 'grouping' &&
        position === 'node' &&
        to.nodeType !== 'stage') ||
      // If node has children, it can't be dropped on a non-grouping
      (position === 'node' &&
        to.nodeType !== 'grouping' &&
        to.nodeType !== 'stage' &&
        ['task', 'event', 'coordination', 'item'].includes(to.nodeType) &&
        from.children.length > 0) ||
      // If node is not a child of another node, it can be dropped on
      (position === 'node' &&
        to.nodeType !== 'grouping' &&
        to.nodeType !== 'stage' &&
        ['task', 'event', 'coordination', 'item'].includes(to.nodeType) &&
        ['task', 'event', 'coordination', 'item'].includes(
          to.parentNodeType
        )) ||
      // Items cannot be dropped next/prev to groupings
      (position !== 'node' &&
        !(
          ['grouping', 'task', 'event', 'coordination', 'item'].includes(
            from.nodeType
          ) &&
          ['grouping', 'task', 'event', 'coordination', 'item'].includes(
            to.nodeType
          )
        )) ||
      /////
      (from.nodeType === 'grouping' && to.nodeType === 'grouping') ||
      (from.nodeType === 'stage' && to.nodeType === 'stage')
    ) {
      return false;
    } else {
      // Check that we are not dropping into one of our children
      return isNotChild({ from, to, position });
    }
  };

  const canAccept = ({ nodeType }: DnDNodeProps) => {
    switch (nodeType) {
      case 'stage':
        return ['stage', 'grouping', 'task', 'event', 'coordination', 'item'];
      case 'grouping':
        return ['grouping', 'task', 'event', 'coordination', 'item'];
      case 'item':
        return ['task', 'event', 'coordination', 'item'];
      case 'task':
        return ['task', 'event', 'coordination', 'item'];
      case 'event':
        return ['task', 'event', 'coordination', 'item'];
      case 'coordination':
        return ['task', 'event', 'coordination', 'item'];
      default:
        return [];
    }
  };

  return (
    <ProjectDnDContext.Provider
      value={{
        itemsRef: itemsRef,
        tree: _tree,
        onMoveComplete,
        styles,
        render,
        moveNode,
        isDroppable,
        canAccept,
        onNodeClick,
        onNodeDoubleClick,
        onContextMenu,
        onClickAway,
        disabled,
      }}
    >
      {children}
    </ProjectDnDContext.Provider>
  );
}

export const useProjectDnDContext = () => {
  return useContext(ProjectDnDContext);
};

export const useProjectDnDStyles = () => {
  const { styles } = useProjectDnDContext();

  return styles || defaultStyles;
};

export const useProjectDnDDefaultStyles = () => {
  const theme = useTheme();
  const downSm = useMediaQuery(theme.breakpoints.down('sm'));

  const projectDnDDefaultStyles = {
    ...defaultStyles,
    containerStyle: {
      paddingLeft: downSm ? 0 : 40,
    },
  };

  return projectDnDDefaultStyles;
};
